import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Chip,
  Avatar,
  Tooltip,
  InputAdornment,
  FormHelperText,
  OutlinedInput,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
  Image as ImageIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
// Firestore 相关导入
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
} from 'firebase/firestore';
// Storage 相关导入
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage, auth } from '../../firebase/firebase'

const FortunetellerManagement = () => {
  // 状态管理
  const [fortuneTellers, setFortuneTellers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openForm, setOpenForm] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedFortuneTeller, setSelectedFortuneTeller] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [openReviewsDialog, setOpenReviewsDialog] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  // 表单初始状态
  const initialFormState = {
    name: '',
    title: '',
    photoURL: '',
    rating: 5.0,
    reviewCount: 0,
    price: 0,
    specialties: [],
    featured: false,
    experience: 0,
    bio: '',
    status: 'active',
    availability: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    },
  };

  const [formData, setFormData] = useState(initialFormState);

  // 每周时间段选项
  const timeSlots = [
    '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', 
    '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00'
  ];

  // 专长选项
  const specialtyOptions = [
    '塔罗牌',
    '占星',
    '八字',
    '面相',
    '手相',
    '风水',
    '紫微斗数',
    '六爻',
    '梅花易数'
  ];

  // 加载占卜师数据
  const loadFortuneTellers = async (reset = false) => {
    try {
      setLoading(reset);
      if (reset) {
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      let fortuneTellersQuery = collection(db, 'fortuneTellers');
      let constraints = [];

      if (statusFilter !== 'all') {
        constraints.push(where('status', '==', statusFilter));
      }

      constraints.push(orderBy('createdAt', 'desc'));
      constraints.push(limit(10));

      if (lastVisible && !reset) {
        constraints.push(startAfter(lastVisible));
      }

      const q = query(fortuneTellersQuery, ...constraints);
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setHasMore(false);
        setLoadingMore(false);
        setLoading(false);
        return;
      }

      const newLastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastVisible(newLastVisible);

      const newFortuneTellers = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (reset) {
        setFortuneTellers(newFortuneTellers);
      } else {
        setFortuneTellers(prev => [...prev, ...newFortuneTellers]);
      }

      setHasMore(querySnapshot.docs.length === 10);
    } catch (error) {
      console.error('加载占卜师失败:', error);
      setSnackbar({
        open: true,
        message: '加载占卜师失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // 初始加载和过滤器变化时重新加载
  useEffect(() => {
    loadFortuneTellers(true);
  }, [statusFilter]);

  // 处理搜索
  const handleSearch = () => {
    if (searchTerm.trim() !== '') {
      const filtered = fortuneTellers.filter(
        fortuneTeller => 
          fortuneTeller.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          fortuneTeller.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFortuneTellers(filtered);
      setHasMore(false);
    } else {
      loadFortuneTellers(true);
    }
  };

  // 处理表单输入变化
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // 清除字段错误
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null,
      });
    }
  };

  // 处理价格和经验年数输入
  const handleNumberInputChange = (e) => {
    const { name, value } = e.target;
    const numberValue = parseFloat(value);
    
    if (!isNaN(numberValue) && numberValue >= 0) {
      setFormData({
        ...formData,
        [name]: numberValue,
      });
      
      // 清除字段错误
      if (formErrors[name]) {
        setFormErrors({
          ...formErrors,
          [name]: null,
        });
      }
    }
  };

  // 处理专长多选
  const handleSpecialtiesChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      specialties: typeof value === 'string' ? value.split(',') : value,
    });
  };

  // 处理开关控件变化
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked,
    });
  };

  // 处理每周可用时间变化
  const handleAvailabilityChange = (day, value) => {
    setFormData({
      ...formData,
      availability: {
        ...formData.availability,
        [day]: value,
      },
    });
  };

  // 处理图片选择
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 验证文件类型
    if (!file.type.match('image.*')) {
      setSnackbar({
        open: true,
        message: '请上传图片文件 (JPEG, PNG)',
        severity: 'error',
      });
      return;
    }

    // 设置文件和创建预览
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      setImagePreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };
  
  // 处理图片上传 - 使用HTTP API上传
  const handleImageUpload = async () => {
    if (!imageFile) {
      setSnackbar({
        open: true,
        message: '请先选择图片',
        severity: 'error',
      });
      return;
    }

    try {
      setUploading(true);
      setError(null);
      
      // 读取文件为base64
      const reader = new FileReader();
      reader.readAsDataURL(imageFile);
      
      // 等待文件读取完成
      const base64Data = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
      
      // 从data:image/jpeg;base64,/9j/4AAQSkZ... 格式中提取base64部分
      const imageData = base64Data.split(',')[1];
      
      // 设置上传URL - 使用实际的Cloud Function URL
      // 获取当前Firebase项目ID
      const projectId = 'destinycrystal-ff557';
      const region = 'asia-southeast1';
      const uploadUrl = `https://${region}-${projectId}.cloudfunctions.net/storage/uploadImage`;
      
      // 执行上传请求
      console.log('正在上传到:', uploadUrl);
      
      // 获取当前用户的身份验证令牌
      const currentUser = auth.currentUser;
      let idToken = '';
      
      if (currentUser) {
        idToken = await currentUser.getIdToken(true);
      } else {
        throw new Error('用户未登录，无法上传图片');
      }
      
      // 添加身份验证令牌到请求头
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        credentials: 'omit', // 改为 'omit' 解决CORS问题
        mode: 'cors',
        body: JSON.stringify({ 
          imageData,
          folder: 'fortuneteller' // 指定存储文件夹为fortuneteller
        })
      });
      
      // 检查状态
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || '上传失败');
      }
      
      // 解析响应
      const responseData = await response.json();
      
      if (responseData.success) {
        // 获取下载URL
        const downloadURL = responseData.imageUrl;
        
        // 设置头像URL
        setFormData({
          ...formData,
          photoURL: downloadURL,
        });
        
        // 更新预览
        setImagePreview(downloadURL);
        
        // 清除暂存的文件
        setImageFile(null);
        
        // 清除相关错误
        if (formErrors.photoURL) {
          setFormErrors({
            ...formErrors,
            photoURL: null,
          });
        }

        setSnackbar({
          open: true,
          message: '头像上传成功',
          severity: 'success',
        });
      } else {
        throw new Error(responseData.message || '上传失败');
      }
    } catch (error) {
      console.error('图片上传失败:', error);
      setSnackbar({
        open: true,
        message: '图片上传失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  // 打开表单添加新占卜师
  const handleAddNew = () => {
    setFormData(initialFormState);
    setImagePreview(null);
    setSelectedFortuneTeller(null);
    setFormErrors({});
    setOpenForm(true);
  };

  // 打开表单编辑占卜师
  const handleEdit = async (id) => {
    try {
      setLoading(true);
      const fortuneTellerDoc = await getDoc(doc(db, 'fortuneTellers', id));
      
      if (fortuneTellerDoc.exists()) {
        const fortunetellerData = fortuneTellerDoc.data();
        setFormData({
          name: fortunetellerData.name || '',
          title: fortunetellerData.title || '',
          photoURL: fortunetellerData.photoURL || '',
          rating: fortunetellerData.rating || 5.0,
          reviewCount: fortunetellerData.reviewCount || 0,
          price: fortunetellerData.price || 0,
          specialties: fortunetellerData.specialties || [],
          featured: fortunetellerData.featured || false,
          experience: fortunetellerData.experience || 0,
          bio: fortunetellerData.bio || '',
          status: fortunetellerData.status || 'active',
          availability: fortunetellerData.availability || {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: [],
          },
        });
        setImagePreview(fortunetellerData.photoURL || null);
        setSelectedFortuneTeller(id);
        setFormErrors({});
        setOpenForm(true);
      } else {
        setSnackbar({
          open: true,
          message: '找不到占卜师数据',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('加载占卜师数据失败:', error);
      setSnackbar({
        open: true,
        message: '加载占卜师数据失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // 处理占卜师状态快速切换
  const handleStatusToggle = async (id, currentStatus) => {
    try {
      setLoading(true);
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await updateDoc(doc(db, 'fortuneTellers', id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });

      // 更新本地状态
      setFortuneTellers(fortuneTellers.map(item => {
        if (item.id === id) {
          return { ...item, status: newStatus };
        }
        return item;
      }));

      setSnackbar({
        open: true,
        message: `占卜师状态已更新为 ${newStatus === 'active' ? '活跃' : '停用'}`,
        severity: 'success',
      });
    } catch (error) {
      console.error('更新占卜师状态失败:', error);
      setSnackbar({
        open: true,
        message: '更新占卜师状态失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // 验证表单数据
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = '姓名不能为空';
    }
    
    if (!formData.title.trim()) {
      errors.title = '头衔不能为空';
    }
    
    if (!formData.photoURL) {
      errors.photoURL = '请上传头像图片';
    }
    
    if (formData.specialties.length === 0) {
      errors.specialties = '请至少选择一个专长';
    }
    
    if (formData.price <= 0) {
      errors.price = '价格必须大于0';
    }
    
    if (formData.experience < 0) {
      errors.experience = '经验年数不能为负数';
    }
    
    if (!formData.bio.trim()) {
      errors.bio = '个人简介不能为空';
    }
    
    // 检查是否有任何一天设置了可用时间
    const hasAvailability = Object.values(formData.availability).some(
      times => times.length > 0
    );
    
    if (!hasAvailability) {
      errors.availability = '请至少设置一天的可用时间';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 提交表单
  const handleSubmit = async () => {
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: '请修正表单错误',
        severity: 'error',
      });
      return;
    }
    
    // 检查是否已经上传了头像 - 检查formData中的photoURL
    if (!formData.photoURL) {
      setSnackbar({
        open: true,
        message: '请先上传头像图片',
        severity: 'error',
      });
      setFormErrors({
        ...formErrors,
        photoURL: '请先上传头像图片'
      });
      return;
    }
    
    try {
      setLoading(true);
      
      // 准备数据
      const fortuneTellerData = {
        name: formData.name,
        title: formData.title,
        photoURL: formData.photoURL,  // 使用上传后的URL
        price: Number(formData.price),
        experience: Number(formData.experience || 0),
        status: formData.status,
        featured: formData.featured,
        specialties: formData.specialties,
        bio: formData.bio,
        availability: formData.availability,
        updatedAt: serverTimestamp(),
      };
      
      if (selectedFortuneTeller) {
        // 更新现有占卜师
        await updateDoc(
          doc(db, 'fortuneTellers', selectedFortuneTeller),
          fortuneTellerData
        );
        
        setSnackbar({
          open: true,
          message: '占卜师信息已更新',
          severity: 'success',
        });
      } else {
        // 添加新占卜师
        const newFortunetellerRef = doc(collection(db, 'fortuneTellers'));
        await setDoc(newFortunetellerRef, {
          ...fortuneTellerData,
          createdAt: serverTimestamp(),
        });
        
        setSnackbar({
          open: true,
          message: '新占卜师已添加',
          severity: 'success',
        });
      }
      
      setOpenForm(false);
      loadFortuneTellers(true);
    } catch (error) {
      console.error('保存占卜师数据失败:', error);
      setSnackbar({
        open: true,
        message: '保存占卜师数据失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // 确认删除之前
  const handleDeleteConfirm = (id) => {
    setSelectedFortuneTeller(id);
    setOpenDeleteDialog(true);
  };

  // 处理删除占卜师
  const handleDelete = async () => {
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'fortuneTellers', selectedFortuneTeller));
      
      // 更新本地状态
      setFortuneTellers(fortuneTellers.filter(
        item => item.id !== selectedFortuneTeller
      ));
      
      setSnackbar({
        open: true,
        message: '占卜师已删除',
        severity: 'success',
      });
      
      setOpenDeleteDialog(false);
    } catch (error) {
      console.error('删除占卜师失败:', error);
      setSnackbar({
        open: true,
        message: '删除占卜师失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
      setSelectedFortuneTeller(null);
    }
  };

  // 加载并显示评价
  const handleViewReviews = async (id) => {
    try {
      setLoading(true);
      setSelectedFortuneTeller(id);
      
      // 从评论集合加载数据
      const reviewsSnapshot = await getDocs(
        query(
          collection(db, 'comments'),
          where('targetId', '==', id),
          where('targetType', '==', 'fortuneTeller'),
          orderBy('createdAt', 'desc')
        )
      );
      
      const reviewsData = reviewsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      setReviews(reviewsData);
      setOpenReviewsDialog(true);
    } catch (error) {
      console.error('加载评价失败:', error);
      setSnackbar({
        open: true,
        message: '加载评价失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // 删除评价
  const handleDeleteReview = async (reviewId) => {
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'comments', reviewId));
      
      // 更新本地状态
      setReviews(reviews.filter(review => review.id !== reviewId));
      
      setSnackbar({
        open: true,
        message: '评价已删除',
        severity: 'success',
      });
      
      // 需要更新占卜师的评分和评价数量
      const fortuneTellerId = selectedFortuneTeller;
      const remainingReviews = reviews.filter(review => review.id !== reviewId);
      
      if (remainingReviews.length > 0) {
        const averageRating = (
          remainingReviews.reduce((sum, review) => sum + review.rating, 0) / 
          remainingReviews.length
        ).toFixed(1);
        
        await updateDoc(doc(db, 'fortuneTellers', fortuneTellerId), {
          rating: parseFloat(averageRating),
          reviewCount: remainingReviews.length,
          updatedAt: serverTimestamp(),
        });
        
        // 更新本地状态
        setFortuneTellers(fortuneTellers.map(item => {
          if (item.id === fortuneTellerId) {
            return {
              ...item,
              rating: parseFloat(averageRating),
              reviewCount: remainingReviews.length,
            };
          }
          return item;
        }));
      } else {
        // 如果没有评价了，设置默认值
        await updateDoc(doc(db, 'fortuneTellers', fortuneTellerId), {
          rating: 5.0,
          reviewCount: 0,
          updatedAt: serverTimestamp(),
        });
        
        // 更新本地状态
        setFortuneTellers(fortuneTellers.map(item => {
          if (item.id === fortuneTellerId) {
            return {
              ...item,
              rating: 5.0,
              reviewCount: 0,
            };
          }
          return item;
        }));
      }
    } catch (error) {
      console.error('删除评价失败:', error);
      setSnackbar({
        open: true,
        message: '删除评价失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // 关闭snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  // 根据状态获取颜色
  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'error';
  };

  // 根据状态获取文本
  const getStatusText = (status) => {
    return status === 'active' ? '活跃' : '停用';
  };

  // 已在上面实现了更完善的 handleImageChange

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          占卜师管理
        </Typography>

        {/* 工具栏 */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              label="搜索占卜师"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSearch} edge="end">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
              <InputLabel>状态</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="状态"
              >
                <MenuItem value="all">全部</MenuItem>
                <MenuItem value="active">活跃</MenuItem>
                <MenuItem value="inactive">停用</MenuItem>
              </Select>
            </FormControl>

            <Button
              startIcon={<RefreshIcon />}
              onClick={() => loadFortuneTellers(true)}
              color="primary"
              variant="outlined"
              size="medium"
            >
              刷新
            </Button>
          </Box>

          <Button
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            color="primary"
            variant="contained"
            size="medium"
          >
            添加占卜师
          </Button>
        </Box>

        {/* 占卜师列表表格 */}
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="占卜师列表">
            <TableHead>
              <TableRow>
                <TableCell>姓名</TableCell>
                <TableCell>头像</TableCell>
                <TableCell>专长</TableCell>
                <TableCell align="center">评分</TableCell>
                <TableCell align="center">价格</TableCell>
                <TableCell align="center">状态</TableCell>
                <TableCell align="center">推荐</TableCell>
                <TableCell align="center">年资</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && fortuneTellers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : fortuneTellers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    暂无数据
                  </TableCell>
                </TableRow>
              ) : (
                fortuneTellers.map((fortuneTeller) => (
                  <TableRow key={fortuneTeller.id}>
                    <TableCell>
                      <Typography variant="body1">{fortuneTeller.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {fortuneTeller.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {fortuneTeller.photoURL ? (
                        <Avatar
                          src={fortuneTeller.photoURL}
                          alt={fortuneTeller.name}
                          sx={{ width: 50, height: 50 }}
                        />
                      ) : (
                        <Avatar sx={{ width: 50, height: 50 }}>
                          {fortuneTeller.name.charAt(0)}
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {fortuneTeller.specialties?.slice(0, 3).map((specialty, index) => (
                          <Chip
                            key={index}
                            label={specialty}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                        {fortuneTeller.specialties?.length > 3 && (
                          <Chip
                            label={`+${fortuneTeller.specialties.length - 3}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <StarIcon sx={{ color: 'orange', mr: 0.5 }} />
                        <Typography variant="body2">
                          {fortuneTeller.rating?.toFixed(1)} ({fortuneTeller.reviewCount || 0})
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">S${fortuneTeller.price}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={getStatusText(fortuneTeller.status)}
                        color={getStatusColor(fortuneTeller.status)}
                        size="small"
                        onClick={() => handleStatusToggle(fortuneTeller.id, fortuneTeller.status)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={fortuneTeller.featured}
                        onChange={async () => {
                          try {
                            setLoading(true);
                            await updateDoc(doc(db, 'fortuneTellers', fortuneTeller.id), {
                              featured: !fortuneTeller.featured,
                              updatedAt: serverTimestamp(),
                            });
                            
                            // 更新本地状态
                            setFortuneTellers(fortuneTellers.map(item => {
                              if (item.id === fortuneTeller.id) {
                                return { ...item, featured: !item.featured };
                              }
                              return item;
                            }));
                            
                            setSnackbar({
                              open: true,
                              message: `占卜师${!fortuneTeller.featured ? '已设为' : '已取消'}推荐`,
                              severity: 'success',
                            });
                          } catch (error) {
                            console.error('更新推荐状态失败:', error);
                            setSnackbar({
                              open: true,
                              message: '更新推荐状态失败: ' + error.message,
                              severity: 'error',
                            });
                          } finally {
                            setLoading(false);
                          }
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">{fortuneTeller.experience}年</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title="查看评价">
                          <IconButton
                            color="info"
                            onClick={() => handleViewReviews(fortuneTeller.id)}
                          >
                            <StarIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="编辑">
                          <IconButton
                            color="primary"
                            onClick={() => handleEdit(fortuneTeller.id)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="删除">
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteConfirm(fortuneTeller.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 加载更多按钮 */}
        {hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              onClick={() => loadFortuneTellers(false)}
              disabled={loadingMore}
              variant="outlined"
              startIcon={loadingMore ? <CircularProgress size={20} /> : null}
            >
              {loadingMore ? '加载中...' : '加载更多'}
            </Button>
          </Box>
        )}

        {/* 占卜师表单对话框 */}
        <Dialog
          open={openForm}
          onClose={() => setOpenForm(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {selectedFortuneTeller ? '编辑占卜师' : '添加新占卜师'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              {/* 基本信息 */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="姓名"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="头衔"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                  error={!!formErrors.title}
                  helperText={formErrors.title}
                />
              </Grid>

              {/* 价格和经验 */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="价格"
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleNumberInputChange}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">S$</InputAdornment>,
                  }}
                  error={!!formErrors.price}
                  helperText={formErrors.price}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="经验年数"
                  name="experience"
                  type="number"
                  value={formData.experience}
                  onChange={handleNumberInputChange}
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment: <InputAdornment position="end">年</InputAdornment>,
                  }}
                  error={!!formErrors.experience}
                  helperText={formErrors.experience}
                />
              </Grid>

              {/* 状态和推荐 */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>状态</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    label="状态"
                  >
                    <MenuItem value="active">活跃</MenuItem>
                    <MenuItem value="inactive">停用</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.featured}
                      onChange={handleSwitchChange}
                      name="featured"
                    />
                  }
                  label="推荐占卜师"
                  sx={{ mt: 2 }}
                />
              </Grid>

              {/* 专长多选 */}
              <Grid item xs={12}>
                <FormControl 
                  fullWidth 
                  margin="normal"
                  error={!!formErrors.specialties}
                >
                  <InputLabel>专长</InputLabel>
                  <Select
                    multiple
                    name="specialties"
                    value={formData.specialties}
                    onChange={handleSpecialtiesChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                    label="专长"
                  >
                    {specialtyOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.specialties && (
                    <FormHelperText>{formErrors.specialties}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* 个人简介 */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="个人简介"
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  margin="normal"
                  multiline
                  rows={4}
                  required
                  error={!!formErrors.bio}
                  helperText={formErrors.bio}
                />
              </Grid>

              {/* 头像上传 */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  头像上传
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {imagePreview ? (
                    <Avatar
                      src={imagePreview}
                      alt="头像预览"
                      sx={{ width: 100, height: 100 }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        border: '1px dashed #ccc',
                        borderRadius: '50%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <ImageIcon color="disabled" />
                    </Box>
                  )}
                  <Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        disabled={uploading}
                        startIcon={uploading ? <CircularProgress size={20} /> : null}
                      >
                        {uploading ? '上传中...' : '选择图片'}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </Button>
                      
                      {imageFile && (
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={uploading}
                          startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                          onClick={handleImageUpload}
                        >
                          {uploading ? '上传中...' : '上传图片'}
                        </Button>
                      )}
                      
                      {formErrors.photoURL && (
                        <Typography color="error" variant="caption" display="block">
                          {formErrors.photoURL}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* 每周可用时间 */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  每周可用时间
                </Typography>
                {formErrors.availability && (
                  <Typography color="error" variant="caption" display="block" sx={{ mb: 1 }}>
                    {formErrors.availability}
                  </Typography>
                )}
                <Grid container spacing={2}>
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={day}>
                      <FormControl fullWidth size="small">
                        <InputLabel id={`${day}-label`}>
                          {['周一', '周二', '周三', '周四', '周五', '周六', '周日'][index]}
                        </InputLabel>
                        <Select
                          labelId={`${day}-label`}
                          multiple
                          value={formData.availability[day] || []}
                          onChange={(e) => handleAvailabilityChange(day, e.target.value)}
                          renderValue={(selected) => (
                            selected.length > 2
                              ? `${selected.length}个时段`
                              : selected.join(', ')
                          )}
                          label={['周一', '周二', '周三', '周四', '周五', '周六', '周日'][index]}
                        >
                          {timeSlots.map((timeSlot) => (
                            <MenuItem key={timeSlot} value={timeSlot}>
                              {timeSlot}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenForm(false)}>取消</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              保存
            </Button>
          </DialogActions>
        </Dialog>

        {/* 删除确认对话框 */}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
        >
          <DialogTitle>确认删除</DialogTitle>
          <DialogContent>
            <DialogContentText>
              确定要删除这位占卜师吗？此操作不可撤销。
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>取消</Button>
            <Button
              onClick={handleDelete}
              color="error"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              删除
            </Button>
          </DialogActions>
        </Dialog>

        {/* 评价列表对话框 */}
        <Dialog
          open={openReviewsDialog}
          onClose={() => setOpenReviewsDialog(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>评价管理</DialogTitle>
          <DialogContent dividers>
            {reviews.length === 0 ? (
              <Typography align="center">暂无评价</Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>用户</TableCell>
                      <TableCell>内容</TableCell>
                      <TableCell align="center">评分</TableCell>
                      <TableCell align="center">时间</TableCell>
                      <TableCell align="right">操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reviews.map((review) => (
                      <TableRow key={review.id}>
                        <TableCell>{review.userName || '匿名用户'}</TableCell>
                        <TableCell>{review.content}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <StarIcon sx={{ color: 'orange', mr: 0.5 }} />
                            <Typography variant="body2">
                              {review.rating?.toFixed(1)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {review.createdAt?.toDate
                            ? review.createdAt.toDate().toLocaleDateString()
                            : '未知'}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteReview(review.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenReviewsDialog(false)}>关闭</Button>
          </DialogActions>
        </Dialog>

        {/* 全局提示 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default FortunetellerManagement;